"use client";

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Button } from "../primitives";
import { renderIcon } from "../icons";
import styles from "./SiteNav.module.css";
import type { SiteNavEntry, SiteNavMenu, SiteNavProps } from "./SiteNav.types";

function isMenu(entry: SiteNavEntry): entry is SiteNavMenu {
  return "items" in entry;
}

function Chevron(): ReactElement {
  return (
    <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function MenuItems({ menu }: { menu: SiteNavMenu }) {
  return (
    <ul className={styles.menuItems}>
      {menu.items.map((item) => (
        <li key={item.href}>
          <a className={styles.menuItem} href={item.href}>
            <span className={styles.menuIcon} aria-hidden="true">
              {renderIcon(item.icon)}
            </span>
            <span className={styles.menuCopy}>
              <span className={styles.menuTitle}>{item.title}</span>
              <span className={styles.menuDescription}>{item.description}</span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/**
 * En-tête du site : logo, liens, menus déroulants à deux colonnes, bouton
 * jaune. Reste en haut de l'écran, s'efface quand on descend et revient dès
 * qu'on remonte. En dessous de 1024 px, un bouton ouvre le menu en plein
 * écran (menus en accordéon, bouton en bas). Échap et un clic ailleurs
 * ferment tout. Reprise native de l'en-tête du site PAF Studio (17/09/2026).
 */
export function SiteNav({ logo, logoHref = "/", items, cta, id }: SiteNavProps) {
  const uid = useId();
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState<number | null>(null);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef<HTMLElement>(null);

  // Défilement : masque en descendant, réaffiche en remontant.
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = (): void => {
      const y = window.scrollY;
      setScrolled(y > 0);
      if (Math.abs(y - last) < 8) return;
      setHidden(y > last && y > 120);
      last = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Échap et clic en dehors ferment le menu ouvert.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpenMenu(null);
      setMobileOpen(false);
    };
    const onPointer = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, []);

  // Menu mobile ouvert : la page dessous ne défile plus.
  useEffect(() => {
    document.documentElement.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [mobileOpen]);

  const ctaButton = (className?: string) =>
    cta ? (
      <Button href={cta.href} className={className} icon={cta.icon ? renderIcon(cta.icon) : undefined}>
        {cta.label}
      </Button>
    ) : null;

  return (
    <header
      ref={rootRef}
      id={id}
      className={styles.root}
      data-hidden={(hidden && openMenu === null && !mobileOpen) || undefined}
      data-scrolled={scrolled || undefined}
      data-site-nav="true"
    >
      <div className={styles.bar}>
        <a className={styles.logo} href={logoHref}>
          <img src={logo.src} alt={logo.alt} width={logo.width} height={logo.height} />
        </a>

        <nav className={styles.desktop} aria-label="Menu principal">
          <ul className={styles.entries}>
            {items.map((entry, i) =>
              isMenu(entry) ? (
                <li
                  key={entry.label}
                  className={styles.entry}
                  onPointerEnter={(event) => event.pointerType === "mouse" && setOpenMenu(i)}
                  onPointerLeave={(event) => event.pointerType === "mouse" && setOpenMenu(null)}
                >
                  <button
                    type="button"
                    className={styles.link}
                    aria-expanded={openMenu === i}
                    aria-controls={`${uid}-menu-${i}`}
                    onClick={() => setOpenMenu(openMenu === i ? null : i)}
                  >
                    {entry.label}
                    <Chevron />
                  </button>
                  <div id={`${uid}-menu-${i}`} className={styles.panel} data-open={openMenu === i || undefined} data-site-nav-panel="true">
                    <div className={styles.panelMain}>
                      <MenuItems menu={entry} />
                    </div>
                    {entry.aside ? (
                      <div className={styles.panelAside}>
                        {entry.aside.icon ? (
                          <span className={styles.asideIcon} aria-hidden="true">
                            {renderIcon(entry.aside.icon)}
                          </span>
                        ) : null}
                        <p className={styles.asideText}>{entry.aside.text}</p>
                        <Button href={entry.aside.cta.href} icon={renderIcon("calendar")}>
                          {entry.aside.cta.label}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ) : (
                <li key={entry.href} className={styles.entry}>
                  <a className={styles.link} href={entry.href}>
                    {entry.label}
                  </a>
                </li>
              ),
            )}
          </ul>
          {ctaButton(styles.cta)}
        </nav>

        <button
          type="button"
          className={styles.burger}
          aria-expanded={mobileOpen}
          aria-controls={`${uid}-mobile`}
          aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <span className={styles.burgerLines} data-open={mobileOpen || undefined} aria-hidden="true">
            <span />
            <span />
          </span>
        </button>
      </div>

      <nav id={`${uid}-mobile`} className={styles.mobile} data-open={mobileOpen || undefined} aria-label="Menu principal" hidden={!mobileOpen}>
        <ul className={styles.mobileEntries}>
          {items.map((entry, i) =>
            isMenu(entry) ? (
              <li key={entry.label} className={styles.mobileEntry}>
                <button
                  type="button"
                  className={styles.mobileLink}
                  aria-expanded={mobileMenu === i}
                  onClick={() => setMobileMenu(mobileMenu === i ? null : i)}
                >
                  {entry.label}
                  <Chevron />
                </button>
                {mobileMenu === i ? (
                  <div className={styles.mobileMenu}>
                    <MenuItems menu={entry} />
                  </div>
                ) : null}
              </li>
            ) : (
              <li key={entry.href} className={styles.mobileEntry}>
                <a className={styles.mobileLink} href={entry.href} onClick={() => setMobileOpen(false)}>
                  {entry.label}
                </a>
              </li>
            ),
          )}
        </ul>
        {ctaButton(styles.mobileCta)}
      </nav>
    </header>
  );
}
